<template>
  <div
    class="fixed z-30 flex min-h-0 flex-col overflow-hidden transition-[width] duration-300 ease-in-out"
    :class="[
      open ? 'w-64' : 'w-14',
      props.isNonProd ? 'top-7 bottom-0' : 'inset-y-0',
    ]"
  >
    <div class="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-fp-green-8">
      <div
        class="flex shrink-0 px-3 pt-5 pb-3"
        :class="open ? 'justify-between' : 'justify-center px-1'"
      >
        <NuxtLink v-show="open" to="/" class="flex gap-2 items-center">
          <FoundersPledgeIcon class="h-10 w-10" />
          <span class="text-primary-5 text-lg font-medium font-public-sans"
            >Navigator</span
          >
        </NuxtLink>
        <button
          type="button"
          class="inline-flex h-10 w-10 shrink-0 items-center justify-center text-[#e1ece5] hover:bg-[#243a2d] focus:outline-none"
          :aria-expanded="open"
          aria-controls="admin-sidebar-nav"
          :aria-label="open ? 'Collapse sidebar' : 'Expand sidebar'"
          @click.stop="open = !open"
        >
          <SidebarLayoutToggleIcon class="h-6 w-6" />
        </button>
      </div>

      <nav
        v-show="open"
        id="admin-sidebar-nav"
        class="admin-sidebar-nav-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2"
      >
        <div class="mt-1 flex items-center justify-between gap-2 py-2">
          <NavNuxtLink
            to="/"
            data-testid="Home-nav-link"
            active-class=""
            exact-active-class=""
            class="inline-flex h-8 shrink-0 items-center gap-2 rounded-full border border-fp-green-7 p-2 text-sm text-fp-green-0 transition-colors"
            :class="
              isHomeActive
                ? 'bg-fp-green-7 hover:bg-fp-green-6'
                : 'bg-[#173126] hover:bg-fp-green-7/40'
            "
          >
            <HomeIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Home</span>
          </NavNuxtLink>
          <SidebarToolSearch @open="openSearchModal" />
        </div>
        <div class="flex flex-col pb-2">
          <NavigationLinks :small="true" />
        </div>
      </nav>
      <div v-show="open" class="shrink-0">
        <LogOutLink :small="true" />
        <Profile :small="true" data-testid="profile-nav-link" />
      </div>
    </div>

    <GlobalSearchModal
      v-model:query="sidebarSearchQuery"
      :show="searchModalOpen"
      @close="closeSearchModal"
      @navigate="onSearchNavigate"
    />
  </div>
</template>

<script lang="ts" setup>
import FoundersPledgeIcon from '@fp/member-webapp-shared/components/fp-logos/FoundersPledgeIcon.vue'
import HomeIcon from '@fp/member-webapp-shared/components/icons/streamline/HomeIcon.vue'
import { computed, onBeforeUnmount, onMounted } from 'vue'
import GlobalSearchModal from '~/components/navigation/GlobalSearchModal.vue'
import LogOutLink from '~/components/navigation/LogOutLink.vue'
import NavigationLinks from '~/components/navigation/NavigationLinks.vue'
import NavNuxtLink from '~/components/navigation/NavNuxtLink'
import Profile from '~/components/navigation/Profile.vue'
import SidebarLayoutToggleIcon from '~/components/navigation/SidebarLayoutToggleIcon.vue'
import SidebarToolSearch from '~/components/navigation/SidebarToolSearch.vue'
import { useAdminGlobalSearch } from '~/composables/useAdminGlobalSearch'

const props = defineProps({
  isNonProd: {
    type: Boolean,
  },
})

const open = defineModel<boolean>('open', { required: true })

const route = useRoute()
const isHomeActive = computed(() => route.path === '/')

const {
  isOpen: searchModalOpen,
  query: sidebarSearchQuery,
  open: openSearchModal,
  close: closeSearchModal,
} = useAdminGlobalSearch()

function onSearchNavigate() {
  closeSearchModal()
}

function isSearchHotkey(event: KeyboardEvent) {
  return (
    (event.metaKey || event.ctrlKey) &&
    event.code === 'KeyK' &&
    !event.shiftKey &&
    !event.altKey
  )
}

function onWindowKeydown(event: KeyboardEvent) {
  if (!isSearchHotkey(event)) return
  event.preventDefault()
  openSearchModal()
}

onMounted(() => {
  window.addEventListener('keydown', onWindowKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown)
})
</script>
